// Campagnes de bannières (docs/PLAN-2026-09-22.md, phase 2) : règle de résolution, permanence, UTM.
import type { Campaign } from './types.ts';
import { lower } from './util.ts';

/** Priorité en trois niveaux, stockés en entier (la plus haute gagne). */
export const CAMPAIGN_PRIORITY = { high: 2, medium: 1, low: 0 } as const;
export type CampaignPriority = keyof typeof CAMPAIGN_PRIORITY;
export const PRIORITY_LABELS: Record<CampaignPriority, string> = { high: 'Élevée', medium: 'Moyenne', low: 'Basse' };
export const PRIORITY_HINTS: Record<CampaignPriority, string> = {
  high: 'Événement à mettre en avant coûte que coûte : passe devant tout le reste pendant sa fenêtre.',
  medium: 'Campagne ordinaire : prend le pas sur les bannières permanentes, s’efface devant une priorité élevée.',
  low: 'Bannière de fond (permanente, image de marque) : ne s’affiche que si rien d’autre ne tourne.',
};
export function priorityOf(n: number): CampaignPriority {
  return n >= CAMPAIGN_PRIORITY.high ? 'high' : n >= CAMPAIGN_PRIORITY.medium ? 'medium' : 'low';
}

/** Une seule campagne est visible sur la signature finale : cette phrase est rappelée dans l'écran. */
export const SINGLE_CAMPAIGN_RULE = 'Une seule campagne est visible sur la signature finale.';

const t = (iso: string | null | undefined): number | null => (iso ? new Date(iso).getTime() : null);

/**
 * Fenêtre de dates : `starts_at` nul = déjà commencée, `ends_at` nul = sans fin. Une campagne sans
 * aucune date est une bannière permanente. Fin exclusive.
 */
export function isCampaignLive(c: Campaign, now: Date): boolean {
  if (!c.active) return false;
  const start = t(c.starts_at);
  const end = t(c.ends_at);
  const n = now.getTime();
  return (start === null || start <= n) && (end === null || n < end);
}

export const isPermanent = (c: Campaign): boolean => !c.starts_at && !c.ends_at;

/**
 * Ciblage :
 * - exclude_emails gagne toujours ;
 * - si include_emails est renseigné, SEULES ces adresses sont ciblées (le filtre lieu est ignoré) ;
 * - sinon, brand_ids vide = tous les lieux, sinon les lieux listés.
 * `emails` = adresse principale + adresse d'envoi (une campagne peut viser l'une ou l'autre).
 */
export function campaignTargets(c: Campaign, brandId: string, emails: string[]): boolean {
  const mine = emails.map(lower);
  if (c.exclude_emails.map(lower).some((e) => mine.includes(e))) return false;
  if (c.include_emails.length > 0) return c.include_emails.map(lower).some((e) => mine.includes(e));
  if (c.brand_ids.length === 0) return true;
  return c.brand_ids.includes(brandId);
}

export interface CampaignTarget {
  brandId: string;
  /** adresse principale + adresse d'envoi */
  emails: string[];
  /** boîte sensible (people.no_banner) : aucune campagne, jamais */
  noBanner?: boolean;
}

/**
 * Règle de résolution — une seule campagne retenue pour une personne à un instant donné :
 * `active`, fenêtre de dates, charte, `include_emails`, `exclude_emails`, `no_banner` ; puis tri par
 * priorité décroissante ; à égalité, début le plus récent, puis identifiant. Déterministe et
 * identique pour tout le monde (décision du 22/09/2026 : pas de hachage par personne — à priorité
 * égale, tout le monde voit la même chose).
 */
export function pickCampaign(campaigns: Campaign[], target: CampaignTarget, now: Date): Campaign | null {
  if (target.noBanner) return null;
  const eligible = campaigns.filter((c) => isCampaignLive(c, now) && campaignTargets(c, target.brandId, target.emails));
  eligible.sort((a, b) => b.priority - a.priority || (t(b.starts_at) ?? -Infinity) - (t(a.starts_at) ?? -Infinity) || a.id.localeCompare(b.id));
  return eligible[0] ?? null;
}

/**
 * Une signature installée à la main (mode local) réserve un emplacement de bannière dynamique si au
 * moins une campagne active cible cette personne, à n'importe quelle date (permanente, en cours ou à
 * venir) ; sinon aucun emplacement (rien à y afficher un jour).
 */
export function hasAnyCampaign(campaigns: Campaign[], target: CampaignTarget): boolean {
  if (target.noBanner) return false;
  return campaigns.some((c) => c.active && campaignTargets(c, target.brandId, target.emails));
}

/**
 * Prochaine date à laquelle le résultat de pickCampaign peut changer (début ou fin d'une campagne).
 * Sert à planifier la prochaine poussée Gmail.
 */
export function nextTransition(campaigns: Campaign[], now: Date): Date | null {
  const n = now.getTime();
  let next: number | null = null;
  for (const c of campaigns) {
    if (!c.active) continue;
    for (const d of [c.starts_at, c.ends_at]) {
      const v = t(d);
      if (v !== null && v > n && (next === null || v < next)) next = v;
    }
  }
  return next === null ? null : new Date(next);
}

// ---------------------------------------------------------------- UTM

/** Slug d'un nom de campagne pour utm_campaign : minuscules, sans accent, tirets. */
export function campaignSlug(name: string): string {
  return name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'campagne';
}

/**
 * Lien de redirection avec les UTM automatiques (utm_source=signature, utm_medium=email,
 * utm_campaign=<slug>) quand la campagne l'active. Un paramètre déjà présent dans le lien saisi
 * n'est jamais doublé ni écrasé. Appliqué au moment de la redirection (fonction `go`), jamais figé
 * dans la signature.
 */
export function withUtm(link: string, c: Pick<Campaign, 'name' | 'utm_auto'>): string {
  if (!c.utm_auto) return link;
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return link;
  }
  const wanted: [string, string][] = [['utm_source', 'signature'], ['utm_medium', 'email'], ['utm_campaign', campaignSlug(c.name)]];
  for (const [k, v] of wanted) if (!url.searchParams.has(k)) url.searchParams.set(k, v);
  return url.toString();
}
